/**
 * StickerLayer — port từ src/components/StickerCanvas.tsx (Phase 7).
 *
 * Khác bản gốc:
 *  - Dùng api.ts PHP thay vì supabase + storage upload.
 *  - Upload ảnh sticker qua POST /api/media/upload (Phase J) → lấy url → insert sticker.
 *  - Bỏ Slider shadcn → dùng <input type="range"> thuần.
 *  - Bỏ toast → dùng alert/console.
 *  - useEditMode lấy từ src/static/EditMode.tsx (không phải @/lib/edit-mode).
 *  - Layout/markup/label tiếng Việt giữ y nguyên.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE } from "@/lib/api";
import { useEditMode } from "@/static/EditMode";

export interface StickerRow {
  id: string;
  page_key: string;
  image_url: string;
  x_pct: number;
  y_pct: number;
  width_pct: number;
  scale: number;
  rotation: number;
  z_index: number;
}

async function listStickers(pageKey: string): Promise<StickerRow[]> {
  try { return await api.get<StickerRow[]>("/page-stickers", { page_key: pageKey }); }
  catch { return []; }
}

export function StickerCanvas({ pageKey }: { pageKey: string }) {
  const { editMode } = useEditMode();
  const qc = useQueryClient();
  const stickersKey = ["page_stickers", pageKey] as const;
  const { data: items = [] } = useQuery({
    queryKey: stickersKey,
    queryFn: () => listStickers(pageKey),
  });
  const setItems = (updater: (prev: StickerRow[]) => StickerRow[]) => {
    qc.setQueryData<StickerRow[]>(stickersKey, (prev) => updater(prev ?? []));
  };
  const invalidate = () => qc.invalidateQueries({ queryKey: stickersKey });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  async function handleUpload(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        // Upload file → /api/media/upload (Phase J)
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", "stickers");
        const resp = await fetch(`${API_BASE}/media/upload`, {
          method: "POST", credentials: "include", body: fd,
        });
        if (!resp.ok) throw new Error(`Upload thất bại (${resp.status})`);
        const m = (await resp.json()) as { url: string };
        const row = await api.post<StickerRow>("/page-stickers", {
          page_key: pageKey, image_url: m.url,
          x_pct: 50, y_pct: 50, width_pct: 14, scale: 1, rotation: 0,
          z_index: items.length + 1,
        });
        setItems((s) => [...s, row]);
        setSelectedId(row.id);
      } catch (e: any) {
        console.error("[sticker upload]", e);
        alert("Lỗi thêm sticker: " + (e?.message ?? "unknown"));
      }
    }
    setUploading(false);
    invalidate();
  }

  async function patch(id: string, p: Partial<StickerRow>) {
    setItems((s) => s.map((it) => (it.id === id ? { ...it, ...p } : it)));
    try { await api.put(`/page-stickers/${encodeURIComponent(id)}`, p); }
    catch (e: any) { console.error("[sticker patch]", e); }
  }

  async function remove(id: string) {
    setItems((s) => s.filter((it) => it.id !== id));
    setSelectedId(null);
    try { await api.del(`/page-stickers/${encodeURIComponent(id)}`); }
    catch (e: any) { alert("Lỗi xoá: " + (e?.message ?? "unknown")); }
  }

  const selected = items.find((i) => i.id === selectedId) ?? null;

  // Stretch layer phủ scroll height của parent (giống bản gốc)
  const layerRef = useRef<HTMLDivElement>(null);
  const [layerH, setLayerH] = useState(0);
  useLayoutEffect(() => {
    const layer = layerRef.current;
    const parent = layer?.parentElement;
    if (!layer || !parent) return;
    const prevPos = parent.style.position;
    if (getComputedStyle(parent).position === "static") parent.style.position = "relative";
    let raf = 0;
    let last = 0;
    const update = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const next = parent.scrollHeight;
        if (Math.abs(next - last) > 1) { last = next; setLayerH(next); }
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(parent);
    window.addEventListener("resize", update);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", update);
      parent.style.position = prevPos;
    };
  }, []);

  return (
    <>
      <div
        ref={layerRef}
        className="pointer-events-none absolute left-0 top-0 w-full z-[40]"
        style={{ height: layerH || "100%" }}
        data-edit-keep
        aria-hidden={!editMode}
      >
        {items.map((s) => (
          <StickerItem
            key={s.id}
            sticker={s}
            editing={editMode}
            selected={editMode && selectedId === s.id}
            onSelect={() => setSelectedId(s.id)}
            onMove={(x, y) => patch(s.id, { x_pct: x, y_pct: y })}
          />
        ))}
      </div>

      {editMode && (
        <div
          data-edit-keep
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full border border-border bg-background/95 px-3 py-2 shadow-xl backdrop-blur"
        >
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90">
            {uploading ? <span className="animate-pulse">…</span> : <span>＋</span>}
            Thêm sticker
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif"
              multiple className="hidden"
              onChange={(e) => handleUpload(e.target.files)} />
          </label>
          {selected && (
            <>
              <div className="h-5 w-px bg-border" />
              <div className="flex items-center gap-2 px-2">
                <span className="text-[10px] text-muted-foreground">W</span>
                <input type="range" min={3} max={60} step={0.5} value={selected.width_pct}
                  onChange={(e) => patch(selected.id, { width_pct: Number(e.target.value) })}
                  className="w-28" />
                <span className="w-9 text-right text-[10px] tabular-nums text-muted-foreground">
                  {Math.round(selected.width_pct)}%
                </span>
              </div>
              <div className="flex items-center gap-2 px-2">
                <span className="text-[10px] text-muted-foreground">↻</span>
                <input type="range" min={-180} max={180} step={1} value={selected.rotation}
                  onChange={(e) => patch(selected.id, { rotation: Number(e.target.value) })}
                  className="w-28" />
                <span className="w-9 text-right text-[10px] tabular-nums text-muted-foreground">
                  {Math.round(selected.rotation)}°
                </span>
              </div>
              <button type="button"
                onClick={() => setSelectedId(null)}
                className="rounded px-2 py-1 text-xs hover:bg-muted" title="Bỏ chọn">✕</button>
              <button type="button"
                onClick={() => remove(selected.id)}
                className="rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                title="Xoá sticker">🗑</button>
            </>
          )}
          {!selected && (
            <span className="px-2 text-[10px] text-muted-foreground">
              {items.length === 0 ? "Tải lên ảnh PNG/JPG để bắt đầu" : "Bấm vào sticker để chỉnh"}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function StickerItem({
  sticker, editing, selected, onSelect, onMove,
}: {
  sticker: StickerRow;
  editing: boolean;
  selected: boolean;
  onSelect: () => void;
  onMove: (x: number, y: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  const [pos, setPos] = useState({ x: sticker.x_pct, y: sticker.y_pct });

  useEffect(() => { setPos({ x: sticker.x_pct, y: sticker.y_pct }); },
    [sticker.x_pct, sticker.y_pct]);

  function getLayerRect() {
    const layer = ref.current?.parentElement;
    return layer?.getBoundingClientRect() ?? null;
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!editing) return;
    e.preventDefault(); e.stopPropagation();
    onSelect();
    const rect = getLayerRect();
    if (!rect) return;
    dragRef.current = {
      dx: e.clientX - (rect.left + (pos.x / 100) * rect.width),
      dy: e.clientY - (rect.top  + (pos.y / 100) * rect.height),
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const rect = getLayerRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(100, ((e.clientX - dragRef.current.dx - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - dragRef.current.dy - rect.top)  / rect.height) * 100));
    setPos({ x, y });
  }
  function onPointerUp(e: React.PointerEvent) {
    if (!dragRef.current) return;
    dragRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    onMove(pos.x, pos.y);
  }

  return (
    <div
      ref={ref}
      className={`absolute select-none ${editing ? "pointer-events-auto" : ""} ${selected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
      style={{
        left: `${pos.x}%`,
        top:  `${pos.y}%`,
        width: `${sticker.width_pct}vw`,
        transform: `translate(-50%, -50%) rotate(${sticker.rotation}deg) scale(${sticker.scale})`,
        zIndex: sticker.z_index,
        cursor: editing ? "grab" : "default",
        touchAction: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={(e) => { if (editing) { e.preventDefault(); e.stopPropagation(); onSelect(); } }}
    >
      <img src={sticker.image_url} alt="" draggable={false}
        className="pointer-events-none h-auto w-full drop-shadow-lg" />
    </div>
  );
}
